import { useEffect, useState } from 'react';
import Spinner from '../components/Spinner';
import { api } from '../utils/api';

// ─── Collapsible Section ─────────────────────────────────────────────────────

function Collapsible({ title, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl mb-4 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div>
          <div className="font-semibold text-gray-800 text-sm">{title}</div>
          {subtitle && <div className="text-xs text-gray-400 mt-0.5">{subtitle}</div>}
        </div>
        <span className={`text-gray-400 transition-transform duration-200 text-lg leading-none ${open ? 'rotate-180' : ''}`}>
          ›
        </span>
      </button>
      {open && <div className="border-t border-gray-100">{children}</div>}
    </div>
  );
}

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#14b8a6', '#3b82f6', '#6366f1', '#a855f7',
  '#ec4899', '#64748b',
];

const BASE_URL = '/api';

function getToken() { return localStorage.getItem('ozon_token'); }
function getRole() {
  try {
    const t = getToken();
    if (!t) return null;
    return JSON.parse(atob(t.split('.')[1])).role;
  } catch { return null; }
}

async function apiFetch(path, options = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Hata');
  return data;
}

// ─── Davet Modal ─────────────────────────────────────────────────────────────

function InviteModal({ onClose, onSent }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await apiFetch('/users/invite', { method: 'POST', body: JSON.stringify({ email, role }) });
      onSent(email);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Kullanıcı Davet Et</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">E-posta Adresi</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="kullanici@email.com"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Rol</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'user', label: 'Kullanıcı', desc: 'Siparişleri görüntüler, stok girer' },
                { key: 'admin', label: 'Yönetici', desc: 'Tüm özelliklere erişir' },
              ].map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRole(r.key)}
                  className={`text-left p-3 rounded-lg border-2 transition-colors ${
                    role === r.key ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-sm text-gray-800">{r.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{r.desc}</div>
                </button>
              ))}
            </div>
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5 rounded-lg">{error}</div>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
            <button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg py-2.5 text-sm font-medium">
              {loading ? 'Gönderiliyor...' : '✉ Davet Gönder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Şifre Değiştir Modal ─────────────────────────────────────────────────────

function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.next !== form.confirm) { setError('Yeni şifreler eşleşmiyor'); return; }
    if (form.next.length < 8) { setError('Şifre en az 8 karakter olmalı'); return; }
    if (!/[A-Z]/.test(form.next) || !/[0-9]/.test(form.next)) { setError('Şifre en az bir büyük harf ve bir rakam içermeli'); return; }
    setError('');
    setLoading(true);
    try {
      await apiFetch('/users/me/password', {
        method: 'PATCH',
        body: JSON.stringify({ current_password: form.current, new_password: form.next }),
      });
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Şifre Değiştir</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {done ? (
          <div className="px-6 py-8 text-center">
            <div className="text-3xl mb-2">✅</div>
            <p className="text-gray-700 font-medium">Şifreniz güncellendi</p>
            <button onClick={onClose} className="mt-4 text-blue-600 text-sm hover:underline">Kapat</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {['current', 'next', 'confirm'].map((key) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  {key === 'current' ? 'Mevcut Şifre' : key === 'next' ? 'Yeni Şifre' : 'Yeni Şifre Tekrar'}
                </label>
                <input
                  type="password"
                  required
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>
            ))}
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5 rounded-lg">{error}</div>}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
              <button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg py-2.5 text-sm font-medium">
                {loading ? 'Kaydediliyor...' : 'Güncelle'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Marka Ayarları ──────────────────────────────────────────────────────────

const BRANDING_KEY = 'ozon_branding';

function applyBrandingCache(data) {
  localStorage.setItem(BRANDING_KEY, JSON.stringify(data));
  window.dispatchEvent(new Event('branding-update'));
}

function BrandingSection() {
  const [name, setName]         = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [logo, setLogo]         = useState('');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved_, setSaved_]     = useState(false);
  const [sizeWarn, setSizeWarn] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    api.getSettings()
      .then((s) => {
        setName(s.appName || '');
        setSubtitle(s.appSubtitle || '');
        setLogo(s.logoDataUrl || '');
      })
      .catch(() => {
        // API hatasında localStorage'dan yükle
        try {
          const cached = JSON.parse(localStorage.getItem(BRANDING_KEY)) || {};
          setName(cached.appName || '');
          setSubtitle(cached.appSubtitle || '');
          setLogo(cached.logoDataUrl || '');
        } catch (_) {}
      })
      .finally(() => setLoading(false));
  }, []);

  function handleLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) { setSizeWarn(true); return; }
    setSizeWarn(false);
    const reader = new FileReader();
    reader.onload = (ev) => setLogo(ev.target.result);
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const data = await api.saveSettings({ appName: name, appSubtitle: subtitle, logoDataUrl: logo });
      applyBrandingCache({ appName: data.appName, appSubtitle: data.appSubtitle, logoDataUrl: data.logoDataUrl });
      setSaved_(true);
      setTimeout(() => setSaved_(false), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  function handleRemoveLogo() {
    setLogo('');
  }

  if (loading) return (
    <div className="flex justify-center py-8"><Spinner size="sm" /></div>
  );

  return (
    <div className="px-6 py-5 space-y-5">
        {/* Logo */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-2">Logo</label>
          <div className="flex items-center gap-4">
            {/* Önizleme */}
            <div className="w-14 h-14 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
              {logo
                ? <img src={logo} alt="logo" className="w-full h-full object-contain" />
                : <span className="text-2xl">🏢</span>
              }
            </div>
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer inline-flex items-center gap-1.5 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors">
                📁 Logo Seç
                <input type="file" accept="image/*" className="hidden" onChange={handleLogo} />
              </label>
              {logo && (
                <button onClick={handleRemoveLogo} className="text-xs text-red-400 hover:text-red-600 text-left">
                  Logoyu kaldır
                </button>
              )}
              {sizeWarn && <p className="text-xs text-red-500">Dosya 200 KB'dan küçük olmalı</p>}
              <p className="text-xs text-gray-400">PNG, JPG — maks 200 KB</p>
            </div>
          </div>
        </div>

        {/* Uygulama adı */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Uygulama Adı</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ozon WMS"
              maxLength={32}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Alt Başlık</label>
            <input
              type="text"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="FBS Sipariş Yönetimi"
              maxLength={48}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>

        {/* Önizleme */}
        <div>
          <div className="text-xs font-semibold text-gray-500 mb-2">Önizleme</div>
          <div className="bg-slate-900 rounded-xl px-5 py-4 flex items-center gap-3 w-56">
            {logo
              ? <img src={logo} alt="logo" className="w-8 h-8 object-contain rounded flex-shrink-0" />
              : <div className="w-8 h-8 rounded bg-slate-700 flex items-center justify-center text-sm flex-shrink-0">🏢</div>
            }
            <div className="min-w-0">
              <div className="text-sm font-bold text-white truncate">{name || 'Ozon WMS'}</div>
              <div className="text-xs text-slate-400 truncate">{subtitle || 'FBS Sipariş Yönetimi'}</div>
            </div>
          </div>
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
          {saved_ && <span className="text-sm text-green-600 font-medium">✓ Kaydedildi</span>}
        </div>
      </div>
  );
}

// ─── Ozon API Ayarları ────────────────────────────────────────────────────────

function OzonApiSection() {
  const [form, setForm] = useState({ ozonClientId: '', ozonApiKey: '', ozonApiKeyWrite: '' });
  const [changed, setChanged] = useState({ ozonApiKey: false, ozonApiKeyWrite: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved_, setSaved_]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    api.getSettings()
      .then((s) => setForm({
        ozonClientId:    s.ozonClientId    || '',
        ozonApiKey:      s.ozonApiKey      || '',   // maskeli: ••••••••
        ozonApiKeyWrite: s.ozonApiKeyWrite || '',
      }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function handleChange(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === 'ozonApiKey' || k === 'ozonApiKeyWrite') {
      setChanged((c) => ({ ...c, [k]: true }));
    }
  }

  async function handleSave() {
    setSaving(true); setError(''); setSaved_(false);
    try {
      const payload = { ozonClientId: form.ozonClientId };
      if (changed.ozonApiKey)      payload.ozonApiKey      = form.ozonApiKey;
      if (changed.ozonApiKeyWrite) payload.ozonApiKeyWrite = form.ozonApiKeyWrite;
      await api.saveSettings(payload);
      setChanged({ ozonApiKey: false, ozonApiKeyWrite: false });
      setSaved_(true);
      setTimeout(() => setSaved_(false), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;

  return (
    <div className="px-6 py-5 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Client ID</label>
          <input
            type="text"
            value={form.ozonClientId}
            onChange={(e) => handleChange('ozonClientId', e.target.value)}
            placeholder="4565172"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <p className="text-xs text-gray-400 mt-1">Hassas değil — açık gösterilir</p>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              API Key <span className="font-normal text-gray-400">(okuma)</span>
            </label>
            <input
              type="password"
              value={form.ozonApiKey}
              onChange={(e) => handleChange('ozonApiKey', e.target.value)}
              placeholder={form.ozonApiKey ? '(kayıtlı — değiştirmek için yazın)' : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              API Key Write <span className="font-normal text-gray-400">(ship & etiket işlemleri)</span>
            </label>
            <input
              type="password"
              value={form.ozonApiKeyWrite}
              onChange={(e) => handleChange('ozonApiKeyWrite', e.target.value)}
              placeholder={form.ozonApiKeyWrite ? '(kayıtlı — değiştirmek için yazın)' : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700">
          API key'ler DB'de saklanır ve GET isteklerinde maskelenir. Değiştirmeden bırakırsanız mevcut key korunur.
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <div className="flex items-center gap-3 pt-1">
          <button onClick={handleSave} disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-medium">
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
          {saved_ && <span className="text-sm text-green-600 font-medium">✓ Kaydedildi</span>}
        </div>
      </div>
  );
}

// ─── SMTP Ayarları ───────────────────────────────────────────────────────────

function SmtpSection() {
  const [form, setForm] = useState({
    smtpHost: '', smtpPort: '587', smtpUser: '', smtpPass: '',
    smtpFrom: '', smtpSecure: 'false', appUrl: 'http://localhost:3000',
  });
  const [passChanged, setPassChanged] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [testing, setTesting]         = useState(false);
  const [testResult, setTestResult]   = useState(null);
  const [saved_, setSaved_]           = useState(false);
  const [error, setError]             = useState('');

  useEffect(() => {
    api.getSettings()
      .then((s) => {
        setForm((f) => ({
          ...f,
          smtpHost:   s.smtpHost   || '',
          smtpPort:   s.smtpPort   || '587',
          smtpUser:   s.smtpUser   || '',
          smtpPass:   s.smtpPass   || '',   // maskeli gelir: '••••••••' ya da ''
          smtpFrom:   s.smtpFrom   || '',
          smtpSecure: s.smtpSecure || 'false',
          appUrl:     s.appUrl     || 'http://localhost:3000',
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function handleChange(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === 'smtpPass') setPassChanged(true);
  }

  async function handleSave() {
    setSaving(true); setError(''); setSaved_(false); setTestResult(null);
    try {
      const payload = { ...form };
      // Şifre değiştirilmediyse gönderme (maskeliydi)
      if (!passChanged) delete payload.smtpPass;
      await api.saveSettings(payload);
      setSaved_(true);
      setPassChanged(false);
      setTimeout(() => setSaved_(false), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true); setTestResult(null);
    try {
      const res = await apiFetch('/users/smtp-test');
      setTestResult(res);
    } catch (e) {
      setTestResult({ ok: false, message: e.message });
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <div className="flex justify-center py-8"><Spinner size="sm" /></div>;

  const Field = ({ label, name, type = 'text', placeholder }) => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
      <input
        type={type}
        value={form[name]}
        onChange={(e) => handleChange(name, e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
      />
    </div>
  );

  return (
    <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <Field label="SMTP Sunucu" name="smtpHost" placeholder="smtp.gmail.com" />
          </div>
          <Field label="Port" name="smtpPort" placeholder="587" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Kullanıcı Adı / E-posta" name="smtpUser" placeholder="siz@gmail.com" />
          <Field label="Şifre / Uygulama Şifresi" name="smtpPass" type="password" placeholder={form.smtpPass ? '(kayıtlı)' : 'Şifre girin'} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Gönderen Adres (From)" name="smtpFrom" placeholder="siz@gmail.com" />
          <Field label="Uygulama URL'si" name="appUrl" placeholder="https://siparis.siz.com" />
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => handleChange('smtpSecure', form.smtpSecure === 'true' ? 'false' : 'true')}
              className={`w-10 h-5 rounded-full transition-colors relative ${form.smtpSecure === 'true' ? 'bg-blue-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.smtpSecure === 'true' ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-gray-700">SSL/TLS (port 465)</span>
          </label>
          <span className="text-xs text-gray-400">— kapalıysa STARTTLS kullanılır (port 587)</span>
        </div>

        {/* Gmail notu */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-700">
          <strong>Gmail için:</strong> Google Hesap → Güvenlik → 2 Adımlı Doğrulama açık → Uygulama Şifreleri → "Posta" için şifre oluştur
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}

        {testResult && (
          <div className={`flex items-center gap-2 text-sm px-3 py-2.5 rounded-lg ${testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
            <span>{testResult.ok ? '✅' : '❌'}</span>
            {testResult.message}
          </div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button onClick={handleSave} disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-medium">
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
          <button onClick={handleTest} disabled={testing || saving}
            className="border border-gray-200 hover:bg-gray-50 disabled:opacity-60 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
            {testing ? 'Test ediliyor...' : '🔌 Bağlantıyı Test Et'}
          </button>
          {saved_ && <span className="text-sm text-green-600 font-medium">✓ Kaydedildi</span>}
        </div>
      </div>
  );
}

// ─── Tab Açıklamaları ────────────────────────────────────────────────────────

const TAB_LABELS = {
  awaiting_packaging:    'Bekleyen Siparişler',
  awaiting_registration: 'Kayıt Bekliyor',
  ready_to_ship:         'Sevkiyata Hazır',
  awaiting_deliver:      'Teslim Bekliyor',
  delivering:            'Kargoda',
  delivered:             'Teslim Edildi',
  cancelled:             'İptal',
};

function TabDescriptionsSection() {
  const [descs, setDescs] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.getSettings().then(s => {
      if (s.tabDescriptions) {
        try { setDescs(JSON.parse(s.tabDescriptions)); } catch (_) {}
      }
    }).catch(() => {});
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      await api.saveSettings({ tabDescriptions: JSON.stringify(descs) });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert('Kaydedilemedi: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="px-6 py-5">
      <p className="text-sm text-gray-500 mb-4">Her sipariş sekmesinin altında gösterilecek bilgilendirme mesajı. Boş bırakılırsa gösterilmez.</p>
      <div className="space-y-3">
        {Object.entries(TAB_LABELS).map(([key, label]) => (
          <div key={key}>
            <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
            <input
              type="text"
              value={descs[key] || ''}
              onChange={e => setDescs(prev => ({ ...prev, [key]: e.target.value }))}
              placeholder={`${label} sekmesi için açıklama...`}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button onClick={handleSave} disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium">
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
        {saved && <span className="text-sm text-green-600">✓ Kaydedildi</span>}
      </div>
    </div>
  );
}

// ─── Google Sheets Entegrasyonu ──────────────────────────────────────────────

function GoogleSheetsSection() {
  const [form, setForm] = useState({
    googleScriptUrl:    '',
    googleStoreName:    '',
    googleSheets:       [],
  });
  const [secretInput,  setSecretInput]  = useState('');   // yeni girilen key
  const [secretSaved,  setSecretSaved]  = useState(false); // DB'de kayıtlı mı?
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [newSheet, setNewSheet] = useState({ name: '', id: '' });

  useEffect(() => {
    api.getSettings().then(s => {
      setForm({
        googleScriptUrl: s.googleScriptUrl  || '',
        googleStoreName: s.googleStoreName  || '',
        googleSheets:    (() => { try { return JSON.parse(s.googleSheets || '[]'); } catch { return []; } })(),
      });
      // Maskelenmiş değer geliyorsa DB'de kayıtlı demek
      setSecretSaved(s.googleScriptSecret === '••••••••' || !!s.googleScriptSecret);
      setSecretInput('');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    setSaving(true); setSaved(false);
    try {
      const payload = {
        googleScriptUrl:  form.googleScriptUrl,
        googleStoreName:  form.googleStoreName,
        googleSheets:     JSON.stringify(form.googleSheets),
      };
      // Sadece yeni bir key girilmişse gönder, boşsa eskiyi koru
      if (secretInput.trim()) payload.googleScriptSecret = secretInput.trim();
      await api.saveSettings(payload);
      if (secretInput.trim()) { setSecretSaved(true); setSecretInput(''); }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { alert('Kaydedilemedi: ' + e.message); }
    finally { setSaving(false); }
  }

  async function handleTest() {
    if (!form.googleScriptUrl) { setTestResult('❌ Önce URL girin ve kaydedin'); return; }
    setTesting(true); setTestResult('');
    try {
      const res = await apiFetch('/settings/google-sheets/test');
      setTestResult(res.ok ? '✅ ' + (res.message || 'Bağlantı başarılı') : '❌ ' + res.error);
    } catch (e) { setTestResult('❌ ' + e.message); }
    finally { setTesting(false); }
  }

  function addSheet() {
    if (!newSheet.name.trim() || !newSheet.id.trim()) return;
    setForm(f => ({ ...f, googleSheets: [...f.googleSheets, { ...newSheet }] }));
    setNewSheet({ name: '', id: '' });
  }
  function removeSheet(i) {
    setForm(f => ({ ...f, googleSheets: f.googleSheets.filter((_, idx) => idx !== i) }));
  }

  if (loading) return null;

  return (
    <div className="px-6 py-5 space-y-5">
      <p className="text-sm text-gray-500">
        Seçili siparişleri Google Sheet'e aktarmak için Apps Script Web App bağlantısı.
      </p>

      {/* Script URL */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Apps Script URL</label>
        <div className="flex gap-2">
          <input type="text" value={form.googleScriptUrl}
            onChange={e => setForm(f => ({ ...f, googleScriptUrl: e.target.value }))}
            placeholder="https://script.google.com/macros/s/.../exec"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 font-mono" />
          <button onClick={handleTest} disabled={testing || !form.googleScriptUrl}
            className="border border-gray-200 hover:bg-gray-50 disabled:opacity-50 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 whitespace-nowrap">
            {testing ? '…' : 'Test Et'}
          </button>
        </div>
        {testResult && <p className="text-xs mt-1.5">{testResult}</p>}
      </div>

      {/* Secret Key */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Secret Key</label>
        {secretSaved && !secretInput && (
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs text-green-600 font-medium">✓ Kayıtlı</span>
            <span className="text-xs text-gray-400">— değiştirmek için yeni key girin</span>
          </div>
        )}
        <input type="password" value={secretInput}
          onChange={e => setSecretInput(e.target.value)}
          placeholder={secretSaved ? 'Değiştirmek için yeni key yazın...' : 'Apps Script\'teki SECRET_KEY ile aynı'}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 font-mono" />
      </div>

      {/* Mağaza adı / Kolon başlığı */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Sheet'teki Kolon Adı (Mağaza Adı)</label>
        <input type="text" value={form.googleStoreName}
          onChange={e => setForm(f => ({ ...f, googleStoreName: e.target.value }))}
          placeholder="Örn: Murat, BSW, Mağaza1..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
        <p className="text-xs text-gray-400 mt-1">Sheet'in başlık satırındaki kolon adı — posting numaraları bu kolonun altına yazılır</p>
      </div>

      {/* Sheet Listesi */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-2">Google Sheet Listesi</label>
        <div className="space-y-1.5 mb-3">
          {form.googleSheets.length === 0 && (
            <p className="text-xs text-gray-400">Henüz sheet eklenmedi</p>
          )}
          {form.googleSheets.map((s, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <span className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">{s.name}</span>
              <span className="text-xs text-gray-400 font-mono flex-1 truncate">{s.id}</span>
              <button onClick={() => removeSheet(i)} className="text-gray-300 hover:text-red-400 text-sm">✕</button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="text" value={newSheet.name}
            onChange={e => setNewSheet(s => ({ ...s, name: e.target.value }))}
            placeholder="Liste adı (örn: Yurtiçi)"
            className="w-36 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
          <input type="text" value={newSheet.id}
            onChange={e => setNewSheet(s => ({ ...s, id: e.target.value }))}
            placeholder="Google Sheet ID"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 font-mono" />
          <button onClick={addSheet} disabled={!newSheet.name.trim() || !newSheet.id.trim()}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
            + Ekle
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1.5">
          Sheet ID: Google Sheets URL'sindeki <code className="bg-gray-100 px-1 rounded">spreadsheets/d/<strong>BU_KISIM</strong>/edit</code>
        </p>
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
        <button onClick={handleSave} disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium">
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
        {saved && <span className="text-sm text-green-600">✓ Kaydedildi</span>}
      </div>
    </div>
  );
}

// ─── Tedarikçiler (Tedarik Takibi kaynakları) ────────────────────────────────

function ScanSourcesSection() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  async function load() {
    setLoading(true);
    try {
      const r = await fetch('/api/scan/sources', { headers: { Authorization: `Bearer ${getToken()}` } });
      setSources(await r.json());
    } catch { setSources([]); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function add() {
    if (!newName.trim()) return;
    setSaving(true); setError('');
    try {
      const r = await fetch('/api/scan/sources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      setNewName('');
      load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  // scan_sources'ın anahtarı name (id kolonu yok)
  async function remove(name) {
    if (!window.confirm('Bu kaynağı silmek istiyor musun?')) return;
    await fetch(`/api/scan/sources/${encodeURIComponent(name)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    load();
  }

  return (
    <div className="px-6 py-5 space-y-4">
      <p className="text-xs text-gray-400">Tedarik Takibi ekranında seçilebilecek tedarikçiler. Okutmada varsayılan BSW'dir.</p>

      {loading ? <Spinner size="sm" /> : (
        <div className="flex flex-wrap gap-2">
          {sources.map(s => (
            <div key={s.name} className="flex items-center gap-1.5 bg-gray-100 rounded-xl px-3 py-1.5">
              <span className="text-sm font-medium text-gray-700">{s.name}</span>
              <button onClick={() => remove(s.name)}
                className="text-gray-300 hover:text-red-400 text-sm leading-none ml-1">✕</button>
            </div>
          ))}
          {sources.length === 0 && <p className="text-sm text-gray-400">Henüz kaynak yok</p>}
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}

      <div className="flex gap-2">
        <input value={newName} onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder="Yeni tedarikçi (örn: BSW)"
          className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
        <button onClick={add} disabled={saving || !newName.trim()}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-40">
          {saving ? '...' : 'Ekle'}
        </button>
      </div>
    </div>
  );
}

// ─── Etiket Yönetimi ─────────────────────────────────────────────────────────

function LabelsSection() {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', color: '#6366f1' });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    try {
      const data = await apiFetch('/labels');
      setLabels(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startEdit(lbl) {
    setEditId(lbl.id);
    setForm({ name: lbl.name, color: lbl.color });
    setError('');
  }

  function cancelEdit() {
    setEditId(null);
    setForm({ name: '', color: '#6366f1' });
    setError('');
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Etiket adı boş olamaz'); return; }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        const updated = await apiFetch(`/labels/${editId}`, {
          method: 'PATCH',
          body: JSON.stringify(form),
        });
        setLabels((prev) => prev.map((l) => (l.id === editId ? updated : l)));
      } else {
        const created = await apiFetch('/labels', { method: 'POST', body: JSON.stringify(form) });
        setLabels((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      }
      cancelEdit();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Bu etiketi silmek istediğinizden emin misiniz? Siparişlerden de kaldırılacak.')) return;
    try {
      await apiFetch(`/labels/${id}`, { method: 'DELETE' });
      setLabels((prev) => prev.filter((l) => l.id !== id));
      if (editId === id) cancelEdit();
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div>
      {/* Yeni / düzenle formu */}
      <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
        <form onSubmit={handleSave} className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-40">
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              {editId ? 'Etiketi Düzenle' : 'Yeni Etiket'}
            </label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Etiket adı"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Renk</label>
            <div className="flex items-center gap-1.5 flex-wrap">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                  className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                    form.color === c ? 'border-gray-800 scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          {/* Önizleme */}
          <div className="flex items-center">
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
              style={{
                backgroundColor: `${form.color}22`,
                color: form.color,
                border: `1px solid ${form.color}44`,
              }}
            >
              {form.name || 'Önizleme'}
            </span>
          </div>
          <div className="flex gap-2">
            {editId && (
              <button
                type="button"
                onClick={cancelEdit}
                className="border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
              >
                İptal
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {saving ? 'Kaydediliyor...' : editId ? 'Güncelle' : '+ Ekle'}
            </button>
          </div>
          {error && <p className="w-full text-xs text-red-500">{error}</p>}
        </form>
      </div>

      {/* Etiket listesi */}
      {loading ? (
        <div className="flex justify-center py-6"><Spinner size="sm" /></div>
      ) : labels.length === 0 ? (
        <div className="px-6 py-6 text-sm text-gray-400 text-center">Henüz etiket oluşturulmadı.</div>
      ) : (
        <div className="divide-y divide-gray-50">
          {labels.map((lbl) => (
            <div key={lbl.id} className="flex items-center justify-between px-6 py-3.5">
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: `${lbl.color}22`,
                  color: lbl.color,
                  border: `1px solid ${lbl.color}44`,
                }}
              >
                {lbl.name}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => startEdit(lbl)}
                  className="text-xs text-gray-400 hover:text-blue-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:border-blue-300 transition-colors"
                >
                  Düzenle
                </button>
                <button
                  onClick={() => handleDelete(lbl.id)}
                  className="text-xs text-red-400 hover:text-red-600 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Sil
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────

export default function Settings() {
  const role = getRole();
  const isAdmin = role === 'admin';
  const currentUsername = localStorage.getItem('ozon_username');

  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  async function load() {
    try {
      if (isAdmin) {
        const [u, inv] = await Promise.all([
          apiFetch('/users'),
          apiFetch('/users/invitations'),
        ]);
        setUsers(u);
        setInvitations(inv);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleUser(user) {
    try {
      const updated = await apiFetch(`/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !user.is_active }),
      });
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
      showToast(`${updated.username} ${updated.is_active ? 'aktif edildi' : 'pasif yapıldı'}`);
    } catch (err) {
      showToast(err.message, 'error');
    }
  }

  async function cancelInvite(id) {
    try {
      await apiFetch(`/users/invitations/${id}`, { method: 'DELETE' });
      setInvitations((prev) => prev.filter((i) => i.id !== id));
      showToast('Davet iptal edildi');
    } catch (err) {
      showToast(err.message, 'error');
    }
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;

  const pendingInvites = invitations.filter((i) => !i.used && new Date(i.expires_at) > new Date());

  return (
    <div className="p-8 max-w-4xl">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-medium shadow-lg ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      <h1 className="text-2xl font-bold text-gray-900 mb-6">Ayarlar</h1>

      {/* ─ Hesabım ─ */}
      <Collapsible title="Hesabım" subtitle={`${currentUsername} · ${isAdmin ? 'Yönetici' : 'Kullanıcı'}`}>
        <div className="px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-lg font-bold text-slate-600 uppercase">
              {currentUsername?.[0]}
            </div>
            <div>
              <div className="font-semibold text-gray-800">{currentUsername}</div>
              <div className="text-sm text-gray-400 mt-0.5">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {isAdmin ? 'Yönetici' : 'Kullanıcı'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowPassword(true)}
            className="border border-gray-200 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium text-gray-600"
          >
            Şifre Değiştir
          </button>
        </div>
      </Collapsible>

      {/* ─ Marka Ayarları ─ */}
      <Collapsible title="Marka Ayarları" subtitle="Sidebar'da görünen isim ve logo">
        <BrandingSection />
      </Collapsible>

      {/* ─ Tedarikçiler ─ */}
      <Collapsible title="Tedarikçiler" subtitle="Tedarik Takibi'nde görünecek tedarikçiler">
        <ScanSourcesSection />
      </Collapsible>

      {/* ─ Etiketler ─ */}
      <Collapsible title="Etiketler" subtitle="Siparişlere atayabileceğiniz özel etiketler">
        <LabelsSection />
      </Collapsible>

      {/* ─ Tab Açıklamaları ─ */}
      <Collapsible title="Sekme Açıklamaları" subtitle="Her sipariş sekmesinde gösterilecek bilgi mesajları">
        <TabDescriptionsSection />
      </Collapsible>

      {/* ─ Google Sheets ─ */}
      {isAdmin && (
        <Collapsible title="Google Sheets Entegrasyonu" subtitle="Seçili siparişleri kargo listesine aktarma">
          <GoogleSheetsSection />
        </Collapsible>
      )}

      {/* ─ Ozon API + SMTP (sadece admin) ─ */}
      {isAdmin && (
        <Collapsible title="Ozon API Ayarları" subtitle="Seller API bilgileri — Client ID ve API key'ler">
          <OzonApiSection />
        </Collapsible>
      )}
      {isAdmin && (
        <Collapsible title="E-posta (SMTP) Ayarları" subtitle="Kullanıcı davetleri için e-posta sunucusu">
          <SmtpSection />
        </Collapsible>
      )}

      {/* ─ Kullanıcılar (sadece admin) ─ */}
      {isAdmin && (
        <>
          <Collapsible title="Kullanıcılar" subtitle={`${users.length} kayıtlı kullanıcı`}>
            <div className="px-6 py-3 border-b border-gray-100 flex justify-end">
              <button
                onClick={() => setShowInvite(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                + Kullanıcı Davet Et
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {users.map((user) => (
                <div key={user.id} className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-500 uppercase flex-shrink-0">
                      {user.username[0]}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800 text-sm">{user.username}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                          user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {user.role === 'admin' ? 'Admin' : 'Kullanıcı'}
                        </span>
                        {!user.is_active && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-600">Pasif</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{user.email}</div>
                      {user.last_login && (
                        <div className="text-xs text-gray-300 mt-0.5">
                          Son giriş: {new Date(user.last_login).toLocaleString('tr-TR')}
                        </div>
                      )}
                    </div>
                  </div>
                  {user.username !== currentUsername && (
                    <button
                      onClick={() => toggleUser(user)}
                      className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                        user.is_active
                          ? 'border-red-200 text-red-500 hover:bg-red-50'
                          : 'border-green-200 text-green-600 hover:bg-green-50'
                      }`}
                    >
                      {user.is_active ? 'Pasif Yap' : 'Aktif Et'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Collapsible>

          {/* ─ Bekleyen Davetler ─ */}
          {pendingInvites.length > 0 && (
            <Collapsible title="Bekleyen Davetler" subtitle={`${pendingInvites.length} bekleyen davet`}>
              <div className="divide-y divide-gray-50">
                {pendingInvites.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between px-6 py-3.5">
                    <div>
                      <div className="text-sm font-medium text-gray-700">{inv.email}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        <span className={`mr-2 ${inv.role === 'admin' ? 'text-purple-500' : 'text-gray-400'}`}>
                          {inv.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
                        </span>
                        Davet eden: {inv.invited_by} ·
                        Bitiş: {new Date(inv.expires_at).toLocaleString('tr-TR')}
                      </div>
                    </div>
                    <button
                      onClick={() => cancelInvite(inv.id)}
                      className="text-xs text-red-400 hover:text-red-600 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50"
                    >
                      İptal Et
                    </button>
                  </div>
                ))}
              </div>
            </Collapsible>
          )}
        </>
      )}

      {/* Modaller */}
      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onSent={(email) => { showToast(`✉ Davet gönderildi: ${email}`); load(); }}
        />
      )}
      {showPassword && <ChangePasswordModal onClose={() => setShowPassword(false)} />}
    </div>
  );
}
